import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import userManager, { signout } from "../../../utils/oidcUserManager";
import { login } from "../_redux/authActions";
import { useLanguageForShared } from "../../../../shared/utils/languages/useLanguageForShared";
import configuration from "../../../../shared/configuration";
import { getUnauthorizedTexts } from "./unauthorized/texts";
import "./unauthorized/unauthorized.css";

/**
 * Pagina "accesso non abilitato", IDENTICA in tutti i frontend (SG, SG Admin, Linde ServiceInsight,
 * CleverFlow, Vivisol): stesso markup e stessa cartella unauthorized/ (css, immagine e font del tema
 * Keycloak, testi), tutta in src perche' /shared allo startup viene sovrascritto.
 *
 * Mostrata quando l'utente E' autenticato su Keycloak ma non puo' usare l'applicazione
 * (`reason`: "app" = claim `available_app` senza REACT_APP_NAME).
 *
 * Casi tipici:
 *  - sul PC c'e' una sessione SSO Keycloak di un ALTRO account (login automatico
 *    senza prompt): per questo mostriamo con quale utente si e' autenticati;
 *  - il permesso e' stato assegnato DOPO il login: i claim in sessione sono vecchi.
 *
 * Riprova: `signinSilent()` (refresh token) e ridispatch della Login con i nuovi claim;
 * se l'app risulta abilitata Routes redirige a "/" da solo. Se il rinnovo silenzioso
 * fallisce si ripiega su un `signinRedirect()` (la sessione SSO rilascia token nuovi
 * senza chiedere credenziali). Al mount il ricontrollo parte in automatico.
 *
 * Cambia utente: logout robusto (`signout`) verso Keycloak.
 */
function ChangeUser({ reason = "app" }) {
  const { lang } = useLanguageForShared();
  const texts = getUnauthorizedTexts(lang || navigator.language);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [checking, setChecking] = useState(true);
  const [retried, setRetried] = useState(false);
  const mounted = useRef(true);
  const logo = `${process.env.REACT_APP_LOAD_CONFIG}assets/images/logo/logo_login.png`;

  const recheck = useCallback(
    async (manual) => {
      setChecking(true);
      try {
        const refreshed = await userManager.signinSilent();
        if (refreshed) {
          dispatch(login(refreshed));
        }
      } catch (error) {
        console.warn("Silent claims refresh failed", error);
        if (manual) {
          await userManager.signinRedirect({ data: { path: "/" } }).catch(console.error);
          return;
        }
      }
      if (mounted.current) {
        setChecking(false);
        setRetried(manual);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    mounted.current = true;
    recheck(false);
    return () => {
      mounted.current = false;
    };
  }, [recheck]);

  return (
    <div className="ae-unauth">
      <div className="ae-unauth__aside">
        <div className="ae-unauth__main">
          <img className="ae-unauth__logo" src={logo} alt="" />
          <div className="ae-unauth__form">
            {checking ? (
              <div className="ae-unauth__checking">
                <span className="ae-unauth__spinner" aria-hidden="true"></span>
                <p className="ae-unauth__text">{texts.checking}</p>
              </div>
            ) : (
              <>
                <div className="ae-unauth__title">{texts.title}</div>
                <p className="ae-unauth__subtitle">
                  {texts.loggedAs}{" "}
                  <span className="ae-unauth__user">{user?.username || texts.unknownUser}</span>
                </p>
                <p className="ae-unauth__text">{texts[`reason_${reason}`]}</p>
                {retried && (
                  <div className="ae-unauth__alert" role="alert">
                    {texts.stillUnauthorized}
                  </div>
                )}
                <p className="ae-unauth__text">{texts.help}</p>
                <div className="ae-unauth__actions">
                  <button
                    type="button"
                    className="ae-unauth__btn ae-unauth__btn--secondary"
                    onClick={() => recheck(true)}
                  >
                    {texts.retry}
                  </button>
                  <button
                    type="button"
                    className="ae-unauth__btn ae-unauth__btn--primary"
                    onClick={() => signout()}
                  >
                    {texts.changeUser}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="ae-unauth__footer">
          © {new Date().getFullYear()} {configuration?.external?.footerLabel || ""}
        </div>
      </div>
      <div className="ae-unauth__image"></div>
    </div>
  );
}

export default ChangeUser;
